/**
 * DevDashboardPage — developer portal for API access and usage analytics.
 *
 * Tabs:
 *  1. Overview  — usage chart (calls/day), top endpoints, quota meter
 *  2. API Keys  — generate, view (masked), revoke keys; reveal on creation
 *  3. Quick Start — code snippets in cURL / Python / JavaScript
 *  4. Account   — tier display, monthly quota, reset date, webhook URL
 *
 * All data falls back to SAMPLE_* constants when the API is unavailable.
 */
import { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import {
  developerApi,
  type ApiKey,
  type ApiKeyCreated,
  type DeveloperTier,
} from '@/lib/api'
import { cn } from '@/lib/utils'
import {
  Key,
  BarChart2,
  Zap,
  User,
  Plus,
  Trash2,
  Copy,
  Check,
  Eye,
  EyeOff,
  AlertTriangle,
  RefreshCw,
  ChevronDown,
  ChevronRight,
  Terminal,
  Code2,
} from 'lucide-react'

// ── Sample data fallbacks ─────────────────────────────────────────────────────

const SAMPLE_KEYS: ApiKey[] = [
  {
    id:         'key_1',
    name:       'Production',
    prefix:     'sk_live_aB3d',
    status:     'active',
    createdAt:  '2026-02-15T10:00:00Z',
    lastUsedAt: '2026-03-28T08:41:00Z',
    callCount:  14837,
  },
  {
    id:         'key_2',
    name:       'Staging',
    prefix:     'sk_live_xK9m',
    status:     'active',
    createdAt:  '2026-03-01T14:20:00Z',
    lastUsedAt: '2026-03-27T21:05:00Z',
    callCount:  3204,
  },
  {
    id:         'key_3',
    name:       'Local dev',
    prefix:     'sk_live_zQ7n',
    status:     'revoked',
    createdAt:  '2026-01-10T09:00:00Z',
    lastUsedAt: '2026-02-20T12:00:00Z',
    callCount:  521,
  },
]

const SAMPLE_USAGE = {
  period:      'last_30_days',
  totalCalls:  18041,
  totalErrors: 137,
  dailySeries: Array.from({ length: 30 }, (_, i) => {
    const d = new Date('2026-02-27')
    d.setDate(d.getDate() + i)
    return {
      date:   d.toISOString().slice(0, 10),
      calls:  Math.floor(300 + Math.random() * 800),
      errors: Math.floor(Math.random() * 15),
    }
  }),
  topEndpoints: [
    { endpoint: 'GET /v1/muhurta/today',         calls: 7421, avgLatencyMs: 42,  errorRate: 0.4 },
    { endpoint: 'GET /v1/charts/me',              calls: 4883, avgLatencyMs: 128, errorRate: 0.7 },
    { endpoint: 'GET /v1/dashboard',              calls: 3102, avgLatencyMs: 95,  errorRate: 0.3 },
    { endpoint: 'POST /v1/compatibility/ashtakoot', calls: 1540, avgLatencyMs: 210, errorRate: 1.1 },
    { endpoint: 'GET /v1/predictions/me',         calls: 1095, avgLatencyMs: 87,  errorRate: 0.6 },
  ],
}

const SAMPLE_ACCOUNT = {
  tier:           'starter' as DeveloperTier,
  callsThisMonth: 18041,
  callLimit:      50000,
  resetDate:      '2026-04-01',
  webhookUrl:     null,
}

// ── Tier config ───────────────────────────────────────────────────────────────

const TIER_META: Record<DeveloperTier, { label: string; color: string; badge: string }> = {
  free:       { label: 'Free',       color: 'text-star-dim',    badge: 'bg-cosmic-dusk text-star-muted' },
  starter:    { label: 'Starter',    color: 'text-auspicious',  badge: 'bg-auspicious/10 text-auspicious border border-auspicious/20' },
  pro:        { label: 'Pro',        color: 'text-gold',        badge: 'bg-gold/10 text-gold border border-gold/20' },
  enterprise: { label: 'Enterprise', color: 'text-violet-light',badge: 'bg-violet/10 text-violet-light border border-violet-mid/30' },
}

// ── Tabs ──────────────────────────────────────────────────────────────────────

type Tab = 'overview' | 'keys' | 'quickstart' | 'account'

const TABS: { id: Tab; label: string; icon: React.ElementType }[] = [
  { id: 'overview',    label: 'Overview',    icon: BarChart2 },
  { id: 'keys',        label: 'API Keys',    icon: Key },
  { id: 'quickstart',  label: 'Quick Start', icon: Zap },
  { id: 'account',     label: 'Account',     icon: User },
]

// ── Helpers ───────────────────────────────────────────────────────────────────

function fmt(n: number): string {
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(1)}M`
  if (n >= 1_000)     return `${(n / 1_000).toFixed(1)}k`
  return String(n)
}

function fmtDate(iso: string): string {
  return new Date(iso).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
}

function fmtRelative(iso: string | null): string {
  if (!iso) return 'Never'
  const diff = Date.now() - new Date(iso).getTime()
  const h = Math.floor(diff / 3_600_000)
  if (h < 1)   return 'Just now'
  if (h < 24)  return `${h}h ago`
  const d = Math.floor(h / 24)
  if (d < 7)   return `${d}d ago`
  return fmtDate(iso)
}

// ── Copy button ───────────────────────────────────────────────────────────────

function CopyButton({ text, className }: { text: string; className?: string }) {
  const [copied, setCopied] = useState(false)
  const copy = () => {
    navigator.clipboard.writeText(text)
    setCopied(true)
    setTimeout(() => setCopied(false), 1800)
  }
  return (
    <button
      onClick={copy}
      className={cn(
        'p-1.5 rounded-md text-star-muted hover:text-star-bright hover:bg-cosmic-aurora transition-colors',
        className,
      )}
      title="Copy to clipboard"
    >
      {copied ? <Check className="w-3.5 h-3.5 text-auspicious" /> : <Copy className="w-3.5 h-3.5" />}
    </button>
  )
}

// ── Stat card ─────────────────────────────────────────────────────────────────

function StatCard({ label, value, sub }: { label: string; value: string; sub?: string }) {
  return (
    <div className="card-cosmic p-4 flex flex-col gap-1">
      <p className="text-2xs font-mono uppercase tracking-wider text-star-dim">{label}</p>
      <p className="text-2xl font-semibold text-star-white tabular-nums">{value}</p>
      {sub && <p className="text-2xs text-star-muted">{sub}</p>}
    </div>
  )
}

// ── Mini bar chart ────────────────────────────────────────────────────────────

function MiniBarChart({ data }: { data: { date: string; calls: number; errors: number }[] }) {
  const max = Math.max(...data.map((d) => d.calls), 1)
  return (
    <div className="flex items-end gap-px h-20" aria-label="Daily API calls chart">
      {data.map((d) => (
        <div
          key={d.date}
          className="flex-1 flex flex-col items-center justify-end gap-px group relative"
          title={`${d.date}: ${d.calls.toLocaleString()} calls, ${d.errors} errors`}
        >
          {/* Error overlay */}
          <div
            className="w-full rounded-t-[1px] bg-inauspicious/60 transition-all"
            style={{ height: `${(d.errors / max) * 100}%` }}
          />
          {/* Calls bar */}
          <div
            className="w-full rounded-t-[2px] bg-gradient-to-t from-violet-mid to-violet-light transition-all group-hover:from-gold/80 group-hover:to-gold"
            style={{ height: `${(d.calls / max) * 80}%` }}
          />
        </div>
      ))}
    </div>
  )
}

// ── Overview tab ──────────────────────────────────────────────────────────────

function OverviewTab({ period, setPeriod }: {
  period: '7d' | '30d' | '90d'
  setPeriod: (p: '7d' | '30d' | '90d') => void
}) {
  const { data: usage = SAMPLE_USAGE, isLoading } = useQuery({
    queryKey: ['dev-usage', period],
    queryFn:  () => developerApi.getUsage(period),
    retry:    1,
  })

  const errorRate = usage.totalCalls > 0
    ? ((usage.totalErrors / usage.totalCalls) * 100).toFixed(2)
    : '0.00'

  const visible = period === '7d' ? usage.dailySeries.slice(-7)
    : period === '30d' ? usage.dailySeries.slice(-30)
    : usage.dailySeries

  return (
    <div className="space-y-5">
      {/* Stats row */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <StatCard label="Total Calls" value={fmt(usage.totalCalls)} sub={`${period.replace('d', ' days')}`} />
        <StatCard label="Errors"      value={fmt(usage.totalErrors)} sub={`${errorRate}% error rate`} />
        <StatCard label="Avg / Day"   value={fmt(Math.round(usage.totalCalls / (period === '7d' ? 7 : period === '30d' ? 30 : 90)))} />
        <StatCard label="Endpoints"   value={String(usage.topEndpoints.length)} sub="active" />
      </div>

      {/* Chart card */}
      <div className="card-cosmic p-5">
        <div className="flex items-center justify-between mb-4">
          <div>
            <p className="text-sm font-semibold text-star-bright">API Calls Over Time</p>
            <p className="text-2xs text-star-dim font-mono mt-0.5">calls per day · red = errors</p>
          </div>
          <div className="flex gap-1 bg-cosmic-void rounded-lg border border-cosmic-mist p-0.5">
            {(['7d', '30d', '90d'] as const).map((p) => (
              <button
                key={p}
                onClick={() => setPeriod(p)}
                className={cn(
                  'px-2.5 py-1 rounded-md text-2xs font-mono transition-colors',
                  period === p
                    ? 'bg-violet-dark text-star-white'
                    : 'text-star-muted hover:text-star-bright',
                )}
              >
                {p}
              </button>
            ))}
          </div>
        </div>
        {isLoading ? (
          <div className="h-20 flex items-center justify-center text-star-dim text-sm">
            <RefreshCw className="w-4 h-4 animate-spin mr-2" /> Loading…
          </div>
        ) : (
          <MiniBarChart data={visible} />
        )}
        {/* X-axis labels */}
        <div className="flex justify-between mt-1.5">
          <span className="text-2xs font-mono text-star-dim">{visible[0]?.date}</span>
          <span className="text-2xs font-mono text-star-dim">{visible[visible.length - 1]?.date}</span>
        </div>
      </div>

      {/* Top endpoints */}
      <div className="card-cosmic p-5">
        <p className="text-sm font-semibold text-star-bright mb-3">Top Endpoints</p>
        <div className="space-y-2">
          {usage.topEndpoints.map((ep, i) => {
            const maxCalls = usage.topEndpoints[0].calls
            const pct = Math.round((ep.calls / maxCalls) * 100)
            return (
              <div key={i} className="group">
                <div className="flex items-center justify-between mb-0.5">
                  <code className="text-2xs font-mono text-star-bright truncate max-w-[55%]">
                    {ep.endpoint}
                  </code>
                  <div className="flex items-center gap-4 text-2xs font-mono text-star-muted shrink-0">
                    <span>{fmt(ep.calls)} calls</span>
                    <span>{ep.avgLatencyMs}ms</span>
                    <span className={cn(ep.errorRate > 1 ? 'text-inauspicious' : 'text-star-dim')}>
                      {ep.errorRate}% err
                    </span>
                  </div>
                </div>
                <div className="h-1 bg-cosmic-void rounded-full overflow-hidden">
                  <div
                    className="h-full bg-gradient-to-r from-violet-mid to-violet-light rounded-full transition-all"
                    style={{ width: `${pct}%` }}
                  />
                </div>
              </div>
            )
          })}
        </div>
      </div>
    </div>
  )
}

// ── API Keys tab ──────────────────────────────────────────────────────────────

function KeysTab() {
  const qc = useQueryClient()
  const [newName, setNewName] = useState('')
  const [createdKey, setCreatedKey] = useState<ApiKeyCreated | null>(null)
  const [revealId, setRevealId] = useState<string | null>(null)
  const [confirmRevoke, setConfirmRevoke] = useState<string | null>(null)
  const [showCreate, setShowCreate] = useState(false)

  const { data: keys = SAMPLE_KEYS, isLoading } = useQuery({
    queryKey: ['dev-keys'],
    queryFn:  developerApi.listKeys,
    retry:    1,
  })

  const createMut = useMutation({
    mutationFn: (name: string) => developerApi.createKey(name),
    onSuccess:  (data) => {
      setCreatedKey(data)
      setNewName('')
      setShowCreate(false)
      qc.invalidateQueries({ queryKey: ['dev-keys'] })
    },
  })

  const revokeMut = useMutation({
    mutationFn: (id: string) => developerApi.revokeKey(id),
    onSuccess:  () => {
      setConfirmRevoke(null)
      qc.invalidateQueries({ queryKey: ['dev-keys'] })
    },
  })

  return (
    <div className="space-y-5">
      {/* Newly created key banner */}
      {createdKey && (
        <div className="card-cosmic border-auspicious/30 bg-auspicious/5 p-4">
          <div className="flex items-start gap-3">
            <Check className="w-4 h-4 text-auspicious mt-0.5 shrink-0" />
            <div className="flex-1 min-w-0">
              <p className="text-sm font-semibold text-star-bright">API key created — save it now</p>
              <p className="text-2xs text-star-muted mt-0.5">This is the only time the full key is shown.</p>
              <div className="mt-2 flex items-center gap-2 bg-cosmic-void rounded-lg px-3 py-2 border border-cosmic-mist">
                <code className="flex-1 text-xs font-mono text-gold break-all">{createdKey.secret}</code>
                <CopyButton text={createdKey.secret} />
              </div>
            </div>
            <button
              onClick={() => setCreatedKey(null)}
              className="text-star-muted hover:text-star-bright transition-colors text-sm leading-none mt-0.5"
            >
              ✕
            </button>
          </div>
        </div>
      )}

      {/* Header row */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-sm font-semibold text-star-bright">API Keys</h2>
          <p className="text-2xs text-star-dim mt-0.5">Keys authenticate requests to the Shubh Time API.</p>
        </div>
        <button
          onClick={() => setShowCreate((v) => !v)}
          className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-violet-dark hover:bg-violet text-star-white text-xs font-medium transition-colors"
        >
          <Plus className="w-3.5 h-3.5" />
          New key
        </button>
      </div>

      {/* Create form */}
      {showCreate && (
        <div className="card-cosmic p-4 flex items-end gap-3">
          <div className="flex-1">
            <label className="block text-2xs font-mono uppercase tracking-wider text-star-dim mb-1.5">
              Key name
            </label>
            <input
              type="text"
              value={newName}
              onChange={(e) => setNewName(e.target.value)}
              placeholder="e.g. Production server"
              className="input-cosmic w-full"
              onKeyDown={(e) => e.key === 'Enter' && newName.trim() && createMut.mutate(newName.trim())}
              autoFocus
            />
          </div>
          <button
            disabled={!newName.trim() || createMut.isPending}
            onClick={() => createMut.mutate(newName.trim())}
            className="px-4 py-2 rounded-lg bg-violet-dark hover:bg-violet text-star-white text-sm font-medium transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
          >
            {createMut.isPending ? <RefreshCw className="w-4 h-4 animate-spin" /> : 'Create'}
          </button>
          <button
            onClick={() => { setShowCreate(false); setNewName('') }}
            className="px-3 py-2 rounded-lg border border-cosmic-mist text-star-muted hover:text-star-bright text-sm transition-colors"
          >
            Cancel
          </button>
        </div>
      )}

      {/* Keys table */}
      <div className="card-cosmic overflow-hidden">
        {isLoading ? (
          <div className="py-12 flex items-center justify-center text-star-dim text-sm">
            <RefreshCw className="w-4 h-4 animate-spin mr-2" /> Loading keys…
          </div>
        ) : keys.length === 0 ? (
          <div className="py-12 text-center text-star-muted text-sm">
            No API keys yet. Create one to get started.
          </div>
        ) : (
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-cosmic-mist">
                {['Name', 'Prefix', 'Status', 'Last Used', 'Total Calls', ''].map((h) => (
                  <th key={h} className="px-4 py-3 text-left text-2xs font-mono uppercase tracking-wider text-star-dim">
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-cosmic-mist/50">
              {keys.map((k) => (
                <tr
                  key={k.id}
                  className={cn(
                    'transition-colors',
                    k.status === 'revoked' ? 'opacity-50' : 'hover:bg-cosmic-dusk/30',
                  )}
                >
                  <td className="px-4 py-3 font-medium text-star-bright">{k.name}</td>
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-1.5">
                      <code className="text-2xs font-mono text-star-muted">
                        {revealId === k.id ? `${k.prefix}••••••••••••` : `${k.prefix.slice(0, 4)}••••••••••••`}
                      </code>
                      {k.status === 'active' && (
                        <button
                          onClick={() => setRevealId(revealId === k.id ? null : k.id)}
                          className="text-star-dim hover:text-star-bright transition-colors"
                          title={revealId === k.id ? 'Hide' : 'Reveal prefix'}
                        >
                          {revealId === k.id
                            ? <EyeOff className="w-3 h-3" />
                            : <Eye className="w-3 h-3" />}
                        </button>
                      )}
                    </div>
                  </td>
                  <td className="px-4 py-3">
                    <span className={cn(
                      'inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-2xs font-medium',
                      k.status === 'active'
                        ? 'bg-auspicious/10 text-auspicious border border-auspicious/20'
                        : 'bg-cosmic-dusk text-star-muted',
                    )}>
                      {k.status === 'active' && <span className="w-1.5 h-1.5 rounded-full bg-auspicious" />}
                      {k.status}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-star-muted text-xs font-mono">{fmtRelative(k.lastUsedAt)}</td>
                  <td className="px-4 py-3 text-star-muted text-xs font-mono tabular-nums">
                    {k.callCount.toLocaleString()}
                  </td>
                  <td className="px-4 py-3 text-right">
                    {k.status === 'active' && (
                      confirmRevoke === k.id ? (
                        <div className="flex items-center justify-end gap-2">
                          <span className="text-2xs text-inauspicious font-mono">Revoke?</span>
                          <button
                            onClick={() => revokeMut.mutate(k.id)}
                            disabled={revokeMut.isPending}
                            className="px-2 py-0.5 rounded text-2xs bg-inauspicious/10 text-inauspicious border border-inauspicious/30 hover:bg-inauspicious/20 transition-colors"
                          >
                            Yes
                          </button>
                          <button
                            onClick={() => setConfirmRevoke(null)}
                            className="px-2 py-0.5 rounded text-2xs text-star-muted border border-cosmic-mist hover:text-star-bright transition-colors"
                          >
                            No
                          </button>
                        </div>
                      ) : (
                        <button
                          onClick={() => setConfirmRevoke(k.id)}
                          className="p-1.5 rounded text-star-muted hover:text-inauspicious hover:bg-inauspicious/10 transition-colors"
                          title="Revoke key"
                        >
                          <Trash2 className="w-3.5 h-3.5" />
                        </button>
                      )
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      <p className="text-2xs text-star-dim font-mono">
        Keys are bound to your account. Never share them publicly or commit them to source control.
      </p>
    </div>
  )
}

// ── Quick Start tab ───────────────────────────────────────────────────────────

type Lang = 'curl' | 'python' | 'javascript'

const SNIPPETS: Record<Lang, string> = {
  curl: `# Fetch today's muhurta slots
curl -X GET "https://api.shubhtime.com/v1/muhurta/today" \\
  -H "Authorization: Bearer YOUR_API_KEY" \\
  -H "Accept: application/json"

# Get Ashtakoot compatibility score
curl -X POST "https://api.shubhtime.com/v1/compatibility/ashtakoot" \\
  -H "Authorization: Bearer YOUR_API_KEY" \\
  -H "Content-Type: application/json" \\
  -d '{
    "name": "Partner Name",
    "birthDate": "1992-06-15",
    "birthTime": "08:30",
    "birthPlace": "Mumbai, India",
    "latitude": 19.076,
    "longitude": 72.877
  }'`,

  python: `import requests

API_KEY = "YOUR_API_KEY"
BASE_URL = "https://api.shubhtime.com/v1"
HEADERS  = {"Authorization": f"Bearer {API_KEY}"}

# Fetch today's muhurta slots
response = requests.get(f"{BASE_URL}/muhurta/today", headers=HEADERS)
slots = response.json()

for slot in slots:
    print(f"{slot['startTime']} — {slot['eventType']} ({slot['quality']})")

# Get compatibility score
partner = {
    "name": "Partner Name",
    "birthDate": "1992-06-15",
    "birthTime": "08:30",
    "birthPlace": "Mumbai, India",
    "latitude": 19.076,
    "longitude": 72.877,
}
compat = requests.post(
    f"{BASE_URL}/compatibility/ashtakoot",
    headers=HEADERS,
    json=partner,
)
print(compat.json()["score"]["totalPoints"], "/ 36")`,

  javascript: `const API_KEY = "YOUR_API_KEY";
const BASE = "https://api.shubhtime.com/v1";

async function getMuhurta(date?: string) {
  const url = date ? \`\${BASE}/muhurta/today?date=\${date}\` : \`\${BASE}/muhurta/today\`;
  const res = await fetch(url, {
    headers: { Authorization: \`Bearer \${API_KEY}\` },
  });
  if (!res.ok) throw new Error(\`HTTP \${res.status}\`);
  return res.json();
}

async function getCompatibility(partner) {
  const res = await fetch(\`\${BASE}/compatibility/ashtakoot\`, {
    method:  "POST",
    headers: {
      Authorization:  \`Bearer \${API_KEY}\`,
      "Content-Type": "application/json",
    },
    body: JSON.stringify(partner),
  });
  return res.json();
}

// Usage
getMuhurta().then((slots) => {
  slots.forEach((s) => console.log(s.startTime, s.eventType));
});`,
}

const LANG_LABELS: Record<Lang, { label: string; icon: React.ElementType }> = {
  curl:       { label: 'cURL',       icon: Terminal },
  python:     { label: 'Python',     icon: Code2 },
  javascript: { label: 'JavaScript', icon: Code2 },
}

function QuickStartTab() {
  const [lang, setLang] = useState<Lang>('curl')
  const [copied, setCopied] = useState(false)

  const copy = () => {
    navigator.clipboard.writeText(SNIPPETS[lang])
    setCopied(true)
    setTimeout(() => setCopied(false), 1800)
  }

  return (
    <div className="space-y-5">
      {/* Intro */}
      <div className="card-cosmic p-5 space-y-2">
        <p className="text-sm font-semibold text-star-bright">Getting started</p>
        <ol className="space-y-2 text-sm text-star-muted list-none">
          {[
            'Go to the API Keys tab and create a key.',
            'Replace YOUR_API_KEY in the snippet below with your key.',
            'All endpoints are at https://api.shubhtime.com/v1 — authenticated with Bearer tokens.',
            'Responses are JSON. Date-times are ISO 8601. Coordinates in decimal degrees.',
          ].map((step, i) => (
            <li key={i} className="flex gap-3">
              <span className="w-5 h-5 rounded-full bg-violet-dark text-star-white text-2xs font-mono flex items-center justify-center shrink-0 mt-0.5">
                {i + 1}
              </span>
              <span>{step}</span>
            </li>
          ))}
        </ol>
      </div>

      {/* Code block */}
      <div className="card-cosmic overflow-hidden">
        {/* Tab bar */}
        <div className="flex items-center border-b border-cosmic-mist px-2 pt-2 gap-1">
          {(Object.keys(LANG_LABELS) as Lang[]).map((l) => {
            const { label, icon: Icon } = LANG_LABELS[l]
            return (
              <button
                key={l}
                onClick={() => setLang(l)}
                className={cn(
                  'flex items-center gap-1.5 px-3 py-1.5 rounded-t-md text-xs font-mono transition-colors border-b-2',
                  lang === l
                    ? 'text-star-bright border-gold bg-cosmic-dusk'
                    : 'text-star-muted border-transparent hover:text-star-bright',
                )}
              >
                <Icon className="w-3.5 h-3.5" />
                {label}
              </button>
            )
          })}
          <div className="flex-1" />
          <button
            onClick={copy}
            className="flex items-center gap-1.5 px-2.5 py-1 mb-1 rounded-md text-2xs font-mono text-star-muted hover:text-star-bright hover:bg-cosmic-aurora transition-colors"
          >
            {copied
              ? <><Check className="w-3 h-3 text-auspicious" /> Copied</>
              : <><Copy className="w-3 h-3" /> Copy</>
            }
          </button>
        </div>
        <pre className="p-5 text-2xs font-mono text-star-bright leading-relaxed overflow-x-auto whitespace-pre bg-cosmic-void">
          <code>{SNIPPETS[lang]}</code>
        </pre>
      </div>

      {/* Base URL */}
      <div className="card-cosmic p-4 flex items-center gap-3">
        <span className="text-2xs font-mono uppercase tracking-wider text-star-dim">Base URL</span>
        <div className="flex-1 flex items-center gap-2 bg-cosmic-void rounded-lg px-3 py-1.5 border border-cosmic-mist">
          <code className="text-xs font-mono text-gold">https://api.shubhtime.com/v1</code>
          <CopyButton text="https://api.shubhtime.com/v1" className="ml-auto" />
        </div>
      </div>
    </div>
  )
}

// ── Account tab ───────────────────────────────────────────────────────────────

function AccountTab() {
  const [webhookDraft, setWebhookDraft] = useState('')
  const [webhookOpen, setWebhookOpen] = useState(false)

  const { data: account = SAMPLE_ACCOUNT } = useQuery({
    queryKey: ['dev-account'],
    queryFn:  developerApi.getAccount,
    retry:    1,
  })

  const tierMeta = TIER_META[account.tier]
  const quotaPct = account.callLimit > 0
    ? Math.min(100, (account.callsThisMonth / account.callLimit) * 100)
    : 0
  const quotaWarn = quotaPct >= 80

  return (
    <div className="space-y-5">
      {/* Tier card */}
      <div className="card-cosmic p-5">
        <div className="flex items-start justify-between mb-4">
          <div>
            <p className="text-2xs font-mono uppercase tracking-wider text-star-dim mb-1">Current plan</p>
            <div className="flex items-center gap-2">
              <span className={cn('px-2.5 py-0.5 rounded-full text-sm font-semibold', tierMeta.badge)}>
                {tierMeta.label}
              </span>
            </div>
          </div>
          <button className="text-xs text-violet-light hover:text-violet-bright font-medium transition-colors">
            Upgrade plan →
          </button>
        </div>

        {/* Quota meter */}
        <div className="space-y-1.5">
          <div className="flex justify-between items-baseline">
            <p className="text-2xs font-mono text-star-dim uppercase tracking-wider">Monthly quota</p>
            <p className={cn('text-2xs font-mono tabular-nums', quotaWarn ? 'text-inauspicious' : 'text-star-muted')}>
              {account.callsThisMonth.toLocaleString()}
              {account.callLimit > 0 ? ` / ${account.callLimit.toLocaleString()}` : ' / ∞'}
            </p>
          </div>
          {account.callLimit > 0 && (
            <div className="h-2 bg-cosmic-void rounded-full overflow-hidden border border-cosmic-mist">
              <div
                className={cn(
                  'h-full rounded-full transition-all',
                  quotaWarn ? 'bg-gradient-to-r from-inauspicious/60 to-inauspicious' : 'bg-gradient-to-r from-violet-mid to-violet-light',
                )}
                style={{ width: `${quotaPct}%` }}
              />
            </div>
          )}
          <div className="flex items-center justify-between">
            {quotaWarn && (
              <p className="text-2xs text-inauspicious flex items-center gap-1">
                <AlertTriangle className="w-3 h-3" />
                {quotaPct >= 100 ? 'Quota exceeded — upgrade to continue.' : `${Math.round(quotaPct)}% used — upgrade soon.`}
              </p>
            )}
            <p className="text-2xs text-star-dim font-mono ml-auto">
              Resets {fmtDate(account.resetDate)}
            </p>
          </div>
        </div>
      </div>

      {/* Plan features */}
      <div className="card-cosmic p-5">
        <p className="text-sm font-semibold text-star-bright mb-3">Plan features</p>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-2">
          {[
            { label: 'API calls / month', value: account.callLimit > 0 ? account.callLimit.toLocaleString() : 'Unlimited' },
            { label: 'Rate limit',        value: account.tier === 'free' ? '60 / min' : account.tier === 'starter' ? '300 / min' : account.tier === 'pro' ? '1,000 / min' : 'Custom' },
            { label: 'Endpoints',         value: account.tier === 'free' ? 'Core only' : 'Full access' },
            { label: 'Webhooks',          value: account.tier === 'free' ? 'Not included' : 'Included' },
            { label: 'SLA',               value: account.tier === 'enterprise' ? '99.99%' : account.tier === 'pro' ? '99.9%' : 'Best effort' },
            { label: 'Support',           value: account.tier === 'enterprise' ? 'Dedicated' : account.tier === 'pro' ? 'Priority' : 'Community' },
          ].map(({ label, value }) => (
            <div key={label} className="flex items-center justify-between py-1.5 border-b border-cosmic-mist/50 last:border-0">
              <span className="text-xs text-star-muted">{label}</span>
              <span className="text-xs font-medium text-star-bright">{value}</span>
            </div>
          ))}
        </div>
      </div>

      {/* Webhook URL */}
      <div className="card-cosmic overflow-hidden">
        <button
          onClick={() => setWebhookOpen((v) => !v)}
          className="w-full flex items-center justify-between px-5 py-4 hover:bg-cosmic-dusk/30 transition-colors"
        >
          <div className="flex items-center gap-2">
            <p className="text-sm font-semibold text-star-bright">Webhook URL</p>
            {account.webhookUrl
              ? <span className="text-2xs bg-auspicious/10 text-auspicious border border-auspicious/20 px-1.5 py-0.5 rounded-full">configured</span>
              : <span className="text-2xs text-star-dim font-mono">optional</span>
            }
          </div>
          {webhookOpen ? <ChevronDown className="w-4 h-4 text-star-muted" /> : <ChevronRight className="w-4 h-4 text-star-muted" />}
        </button>

        {webhookOpen && (
          <div className="px-5 pb-5 space-y-3 border-t border-cosmic-mist">
            <p className="text-xs text-star-muted pt-3">
              Receive POST events when new muhurta windows open, dasha transitions occur, or transit alerts trigger.
            </p>
            <div className="flex gap-2">
              <input
                type="url"
                value={webhookDraft || account.webhookUrl || ''}
                onChange={(e) => setWebhookDraft(e.target.value)}
                placeholder="https://your-server.com/webhook"
                className="input-cosmic flex-1 text-xs"
              />
              <button
                disabled={!webhookDraft.trim()}
                className="px-4 py-2 rounded-lg bg-violet-dark hover:bg-violet text-star-white text-xs font-medium transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
              >
                Save
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}

// ── Page ──────────────────────────────────────────────────────────────────────

export function DevDashboardPage() {
  const [activeTab, setActiveTab] = useState<Tab>('overview')
  const [period, setPeriod] = useState<'7d' | '30d' | '90d'>('30d')

  return (
    <div className="max-w-4xl mx-auto px-4 py-6 space-y-6">
      {/* Page header */}
      <div className="flex items-start justify-between">
        <div>
          <h1 className="text-xl font-semibold text-star-white leading-tight">Developer Dashboard</h1>
          <p className="text-star-dim text-sm mt-1">
            Manage API access, view usage, and integrate Shubh Time into your applications.
          </p>
        </div>
        <span className="text-sanskrit text-2xs mt-1">देव पोर्टल</span>
      </div>

      {/* Tab bar */}
      <div className="flex gap-1 border-b border-cosmic-mist">
        {TABS.map(({ id, label, icon: Icon }) => (
          <button
            key={id}
            onClick={() => setActiveTab(id)}
            className={cn(
              'flex items-center gap-1.5 px-4 py-2.5 text-sm font-medium transition-colors border-b-2 -mb-px',
              activeTab === id
                ? 'text-star-bright border-gold'
                : 'text-star-muted border-transparent hover:text-star-bright hover:border-cosmic-mist',
            )}
          >
            <Icon className="w-4 h-4" />
            {label}
          </button>
        ))}
      </div>

      {/* Tab content */}
      {activeTab === 'overview'   && <OverviewTab period={period} setPeriod={setPeriod} />}
      {activeTab === 'keys'       && <KeysTab />}
      {activeTab === 'quickstart' && <QuickStartTab />}
      {activeTab === 'account'    && <AccountTab />}
    </div>
  )
}
